import { ClientApplication } from '@numeo/types-core'
import { shouldSourceCompanyFromSettings } from '@/pages/dashboard/pages/settings/components/Tabs/GeneralTab/formData'
import { UserCompanyData } from './emailTemplateUtils'

interface CompanyDetailLike {
    name?: string
    mcNumber?: string
    dotNumber?: string
    phoneNumber?: string
    transferNumber?: string
    aiAgentName?: string
    companyAddress?: string
    details?: string
}

interface Args {
    /** The companyDetail of the email the load-hub email is being sent from. */
    selectedCompanyDetail?: CompanyDetailLike
    /** All connected emails — used to decide whether the account is unambiguously single-company. */
    nylasEmails?: Array<{ companyDetail?: { mcNumber?: string } }> | null
    application?: Pick<ClientApplication, 'settings'> | null
    companyFromParentEnabled?: boolean
    userName?: string
    /** Passed straight through — the picked sender's address, for templates that print it. */
    userEmail?: string
    /** Passed straight through — the company logo lifted out of the resolved signature. */
    companyLogoUrl?: string
}

const nonEmpty = (a?: string, b?: string): string => (a && a.trim() ? a : b || '')

/**
 * Resolve the carrier's company data ({{company}}, {{mc}}, {{dot}}, …) for a load-hub outbound email.
 *
 * Per-email `companyDetail` is the default source, but for a migrated account (companyFromParentEnabled)
 * OR an unambiguously single-company account it reads the canonical `settings.company` (filling any gap
 * from the per-email detail). This is what keeps the "Carrier MC#" from going out blank when the
 * per-email detail was never backfilled — e.g. an account verified before the parent-company model —
 * while a genuinely multi-MC account still uses its per-email detail and never borrows another MC.
 */
export const resolveSenderCompanyData = ({
    selectedCompanyDetail,
    nylasEmails,
    application,
    companyFromParentEnabled,
    userName,
    userEmail,
    companyLogoUrl,
}: Args): UserCompanyData => {
    const detail = selectedCompanyDetail
    const company = application?.settings?.company

    if (company && (companyFromParentEnabled || shouldSourceCompanyFromSettings(application, detail, nylasEmails))) {
        return {
            companyName: nonEmpty(company.name, detail?.name),
            mcNumber: nonEmpty(company.mcNumber, detail?.mcNumber),
            dotNumber: nonEmpty(company.dotNumber, detail?.dotNumber),
            phoneNumber: nonEmpty(company.phoneNumber, detail?.phoneNumber),
            transferNumber: nonEmpty(company.transferNumber, detail?.transferNumber),
            aiAgentName: nonEmpty(company.aiAgentName, detail?.aiAgentName),
            companyAddress: nonEmpty(company.companyAddress, detail?.companyAddress),
            details: nonEmpty(company.details, detail?.details),
            userName: userName || '',
            userEmail: userEmail || '',
            companyLogoUrl: companyLogoUrl || '',
        }
    }

    return {
        companyName: detail?.name || '',
        mcNumber: detail?.mcNumber || '',
        dotNumber: detail?.dotNumber || '',
        phoneNumber: detail?.phoneNumber || '',
        transferNumber: detail?.transferNumber || '',
        aiAgentName: detail?.aiAgentName || '',
        companyAddress: detail?.companyAddress || '',
        details: detail?.details || '',
        userName: userName || '',
        userEmail: userEmail || '',
        companyLogoUrl: companyLogoUrl || '',
    }
}
